import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Radio,
  Checkbox,
  Badge,
  Loader,
  Alert,
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCourse, useMyEnrollments, useSubmitQuizAttempt } from '../../../hooks/useApi';
import { PageContainer } from '../../../components/ui/PageContainer';
import { Enrollment, QuizAttemptResult } from '../../../types';

export const QuizPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: course, isLoading: isCourseLoading } = useCourse(id || '');
  const { data: enrollments = [], isLoading: isEnrollmentsLoading } = useMyEnrollments();
  const submitQuiz = useSubmitQuizAttempt();

  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [latestAttempt, setLatestAttempt] = useState<QuizAttemptResult | null>(null);

  const enrollment: Enrollment | undefined = React.useMemo(() => {
    return enrollments.find((e) => {
      const courseId = typeof e.courseId === 'string' ? e.courseId : e.courseId._id;
      return courseId === id;
    });
  }, [enrollments, id]);

  const isAllModulesCompleted = React.useMemo(() => {
    if (!course || !enrollment) return false;
    if (course.modules.length === 0) return true;
    return course.modules.every((mod) =>
      enrollment.moduleProgress.some((mp) => mp.moduleId === mod._id && mp.isCompleted),
    );
  }, [course, enrollment]);

  // Guard: redirect if not enrolled or modules not completed
  useEffect(() => {
    if (!isCourseLoading && !isEnrollmentsLoading) {
      if (!enrollment || !isAllModulesCompleted) {
        navigate(`/learning/${id}`, { replace: true });
      }
    }
  }, [enrollment, isAllModulesCompleted, isCourseLoading, isEnrollmentsLoading, id, navigate]);

  const handleSingleChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: [parseInt(value, 10)] }));
  };

  const handleMultipleChange = (questionId: string, values: string[]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: values.map((v) => parseInt(v, 10)) }));
  };

  const handleSubmit = async () => {
    if (!enrollment || !course) return;

    const formattedAnswers = course.quizQuestions.map((q) => ({
      questionId: q._id,
      selectedOptions: answers[q._id] || [],
    }));

    try {
      const updatedEnrollment = await submitQuiz.mutateAsync({
        enrollmentId: enrollment._id,
        answers: formattedAnswers,
      });

      // Get the latest quiz attempt from the returned enrollment
      const attempts = updatedEnrollment.quizAttempts;
      if (attempts.length > 0) {
        setLatestAttempt(attempts[attempts.length - 1]);
      }
      setIsSubmitted(true);
    } catch {
      // Error handled by mutation
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setIsSubmitted(false);
    setLatestAttempt(null);
  };

  if (isCourseLoading || isEnrollmentsLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </PageContainer>
    );
  }

  if (!course || !enrollment) {
    return null;
  }

  const isPassed = latestAttempt ? latestAttempt.score >= course.passingScore : false;

  return (
    <PageContainer>
      <Stack gap="md">
        <Button
          variant="light"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          onClick={() => navigate(`/learning/${id}`)}
          w="fit-content"
        >
          {t('learning.back')}
        </Button>

        <Title order={2}>
          {t('learning.quiz')}: {course.title}
        </Title>

        {isSubmitted && latestAttempt && (
          <Card withBorder padding="md">
            <Stack gap="sm">
              <Title order={3}>{t('learning.quizResults')}</Title>
              <Group gap="sm">
                <Text fw={500}>
                  {t('learning.score')}: {Math.round(latestAttempt.score)}%
                </Text>
                {isPassed ? (
                  <Badge color="green" size="lg" leftSection={<IconCheck size={14} />}>
                    {t('learning.passed')}
                  </Badge>
                ) : (
                  <Badge color="red" size="lg" leftSection={<IconX size={14} />}>
                    {t('learning.failed')}
                  </Badge>
                )}
              </Group>

              {/* Per-question breakdown */}
              <Stack gap="xs">
                {course.quizQuestions.map((question, qIndex) => {
                  const attemptAnswer = latestAttempt.answers.find(
                    (a) => a.questionId === question._id,
                  );
                  // Верность ответа ставит сервер: ключа ответов у ученика нет.
                  const isCorrect = attemptAnswer?.isCorrect === true;

                  return (
                    <Group key={question._id} gap="xs" align="center">
                      {isCorrect ? (
                        <IconCheck size={16} color="green" />
                      ) : (
                        <IconX size={16} color="red" />
                      )}
                      <Text size="sm">
                        {qIndex + 1}. {question.text}
                      </Text>
                    </Group>
                  );
                })}
              </Stack>

              <Group gap="sm">
                {!isPassed && (
                  <Button variant="outline" onClick={handleRetry}>
                    {t('learning.retryQuiz')}
                  </Button>
                )}
                <Button onClick={() => navigate(`/learning/${id}`)}>{t('learning.back')}</Button>
              </Group>
            </Stack>
          </Card>
        )}

        {!isSubmitted && (
          <Stack gap="md">
            {course.quizQuestions
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((question, qIndex) => (
                <Card key={question._id} withBorder padding="md">
                  <Stack gap="sm">
                    <Text fw={500}>
                      {qIndex + 1}. {question.text}
                    </Text>

                    {question.type === 'single' ? (
                      <Radio.Group
                        value={
                          answers[question._id]?.length > 0 ? String(answers[question._id][0]) : ''
                        }
                        onChange={(value) => handleSingleChange(question._id, value)}
                      >
                        <Stack gap="xs">
                          {question.options.map((option, oIndex) => (
                            <Radio key={oIndex} value={String(oIndex)} label={option.text} />
                          ))}
                        </Stack>
                      </Radio.Group>
                    ) : (
                      <Checkbox.Group
                        value={(answers[question._id] || []).map(String)}
                        onChange={(values) => handleMultipleChange(question._id, values)}
                      >
                        <Stack gap="xs">
                          {question.options.map((option, oIndex) => (
                            <Checkbox key={oIndex} value={String(oIndex)} label={option.text} />
                          ))}
                        </Stack>
                      </Checkbox.Group>
                    )}
                  </Stack>
                </Card>
              ))}

            <Button onClick={handleSubmit} loading={submitQuiz.isPending}>
              {t('learning.submitQuiz')}
            </Button>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};
